import { USGSA_TRACK_SOURCE } from "@/constants/horseRacing";
import { Hr_BettingInterest, Hr_Race, Hr_Runner } from "@/graphql/gql/graphql";
import { filterEmpty } from ".";

function getRunnerSortingWeight(
  runner: Hr_Runner | undefined,
  trackDataSource: string
) {
  // Created an array with the sorted possible combinations,
  // this way we can sort by combination weight (this array position)
  const sortingWeights = [
    "1, null",
    "1, 1",
    "1, 2",
    "1, 3",
    "null, 1",
    "2, null",
    "2, 1",
    "2, 2",
    "2, 3",
    "null, 2",
    "3, null",
    "3, 1",
    "3, 2",
    "3, 3",
    "null, 3",
  ];

  const timeformPickNumber = runner?.timeform?.freePick?.number || null;
  const handicappingPickNumber = runner?.handicapping?.freePick?.number || null;

  if (trackDataSource === USGSA_TRACK_SOURCE) {
    if (timeformPickNumber || handicappingPickNumber) {
      return sortingWeights.indexOf(
        `${timeformPickNumber}, ${handicappingPickNumber}`
      );
    }
  }

  return handicappingPickNumber;
}

function sortByPick(
  bettingInterests: Hr_BettingInterest[],
  trackDataSource: string
) {
  return bettingInterests.sort(
    ({ runners: runnersA }, { runners: runnersB }) =>
      (getRunnerSortingWeight(runnersA?.[0] ?? undefined, trackDataSource) ??
        0) -
      (getRunnerSortingWeight(runnersB?.[0] ?? undefined, trackDataSource) ?? 0)
  );
}

function filterBettingInterests(bettingInterests: Hr_BettingInterest[]) {
  return bettingInterests.filter(
    ({ runners }) =>
      runners?.[0]?.timeform?.freePick || runners?.[0]?.handicapping?.freePick
  );
}

function filterPickSources(
  bettingInterests: Hr_BettingInterest[],
  trackDataSource = ""
) {
  let timeform = false;
  let handicapping = false;

  bettingInterests.forEach(({ runners }) => {
    const runner = runners?.[0];
    if (runner?.timeform?.freePick?.number) {
      timeform = true;
    }

    if (runner?.handicapping?.freePick?.number) {
      handicapping = true;
    }
  });

  if (trackDataSource === USGSA_TRACK_SOURCE && timeform && handicapping) {
    return ["timeform", "handicapping"];
  }

  return ["handicapping"];
}
export function filterCoupledBettingInterests(
  bettingInterests: Hr_BettingInterest[]
) {
  return bettingInterests
    .reduce((acc, bettingInterest) => {
      const { runners: maybeRunners } = bettingInterest;
      const runners = filterEmpty(maybeRunners ?? []);
      return [
        ...acc,
        ...(runners.length > 1
          ? runners.map((runner) => ({ ...bettingInterest, runners: [runner] }))
          : [bettingInterest]),
      ];
    }, [] as Hr_BettingInterest[])
    .sort(({ runners: runnersA }, { runners: runnersB }) => {
      const [runnerA] = runnersA || [];
      const [runnerB] = runnersB || [];
      if (!(runnerA?.runnerId && runnerB?.runnerId)) {
        return 0;
      }
      return runnerA.runnerId && runnerB.runnerId
        ? Number.parseInt(runnerA.runnerId, 10) -
            Number.parseInt(runnerB.runnerId, 10)
        : runnerA.runnerId.localeCompare(runnerB.runnerId);
    });
}

export default function filterPicksByDataSource(races: Hr_Race[]) {
  return races.map((race) => {
    const { track } = race;
    const { trackDataSource } = track ?? {};
    const bettingInterests = filterBettingInterests(
      filterCoupledBettingInterests(filterEmpty(race.bettingInterests ?? []))
    );
    const pickSources = filterPickSources(
      filterCoupledBettingInterests(filterEmpty(race.bettingInterests ?? [])),
      trackDataSource ?? ""
    );

    return {
      ...race,
      bettingInterests: sortByPick(bettingInterests, trackDataSource ?? ""),
      pickSources,
    };
  });
}
